import React, {useEffect, useRef, useState} from 'react';
import {
  Animated,
  Easing,
  Image,
  Keyboard,
  Linking,
  Platform,
  ScrollView,
  TextInput,
  TouchableOpacity,
} from 'react-native';
import {LinearGradient} from 'expo-linear-gradient';
import {useRive, Layout, Fit, Alignment} from '@rive-app/react-canvas';
import caissahello_animation from '@/Assets/Animation/CaissaHello_animation.riv';
import Ionicons from 'react-native-vector-icons/Ionicons';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import Spacer from '@/Atoms/Spacer';
import SlideModal from '@/Atoms/SlideModal';
import styles, {T} from './styles';
import {FontSizes} from '@/Utils/Dimensions';
import useGetCaissaChatbotResponse, {
  useSubmitCaissaReaction,
} from '@/Hooks/CaissaChatbot';
import CCParsedMessage from '@/Atoms/CCParsedMessage';
import TypingDots from '../TypingDots';
import AnswerReactions from '../AnswerReactions';
import type {CaissaReaction} from '@/Hooks/CaissaChatbot';
import {useUserPlanStore, selectIsCsocUser} from '@/Store/userPlanStore';
import {useMediaQuery} from '@/Hooks/useMediaQuery/useMediaQuery';

// Web hover state helper — returns [hovered, handlers-to-spread].
const useHover = () => {
  const [hovered, setHovered] = useState(false);
  const handlers = {
    onMouseEnter: () => setHovered(true),
    onMouseLeave: () => setHovered(false),
  } as any;
  return [hovered, handlers] as const;
};

// A pressable that applies `hoverStyle` while hovered (clickable affordance).
const HoverBox = ({
  onPress,
  style,
  hoverStyle,
  disabled,
  children,
}: {
  onPress: () => void;
  style?: any;
  hoverStyle?: any;
  disabled?: boolean;
  children: React.ReactNode;
}) => {
  const [hovered, handlers] = useHover();
  return (
    <TouchableOpacity onPress={onPress} disabled={disabled} activeOpacity={0.8}>
      <Box {...handlers} style={[style, hovered && !disabled && hoverStyle]}>
        {children}
      </Box>
    </TouchableOpacity>
  );
};

const CAISSA_AVATAR = require('@/Assets/Images/Caissa/Caissa_bot_girl.svg');

type textMsgProp = {
  id: number;
  timeStamp: string;
  msg: string;
  type: 'text' | 'image' | 'pdf' | 'video';
  from: 'user' | 'caissa';
  // Set only for psychology-mode Caissa answers -- enables the reaction row.
  chatLogId?: number | null;
  reaction?: CaissaReaction | null;
};

type Agent = {
  id: string;
  name: string;
  desc: string;
  initial: string;
  color: string;
  colorDark: string;
  kind: 'chat' | 'human';
  psychology?: boolean;
  layout: 'top' | 'row';
  starters?: string[];
};

// Agents map onto today's real functionality: General Coach = normal chess Q&A,
// Psychology Guide = psychology mode, Talk to a Human = WhatsApp hand-off.
const AGENTS: Agent[] = [
  {
    id: 'coach',
    name: 'Chess Coach',
    desc: 'Openings, tactics & game plans',
    initial: 'C',
    color: T.aqua,
    colorDark: T.aquaDark,
    kind: 'chat',
    psychology: false,
    layout: 'top',
    starters: [
      'How do I improve my middle game?',
      'What is the Sicilian Defense?',
      'How do I plan in a closed position?',
      'When should I trade queens?',
    ],
  },
  {
    id: 'psychology',
    name: 'Psychology Coach',
    desc: 'Mindset, nerves & focus',
    initial: 'P',
    color: T.purple,
    colorDark: T.purpleDark,
    kind: 'chat',
    psychology: true,
    layout: 'top',
    starters: [
      'How do I stay calm after a blunder?',
      'How can I control my nerves during tournaments?',
      'How do I build confidence before a big game?',
      'How can I stay focused during long games?',
    ],
  },
  // {
  //   id: 'ops',
  //   name: 'Ops & Support',
  //   desc: 'Plans, School of Chess & help',
  //   initial: 'O',
  //   color: T.ops,
  //   colorDark: T.opsDark,
  //   kind: 'chat',
  //   psychology: false,
  //   layout: 'row',
  //   starters: [
  //     'What are the coaching plans?',
  //     'Tell me about the School of Chess',
  //     'How do I register for a tournament?',
  //     'How can I contact support?',
  //   ],
  // },
  {
    id: 'human',
    name: 'Customer Support',
    desc: 'Chat with a Chess Expert on WhatsApp',
    initial: 'W',
    color: T.wa,
    colorDark: T.waDark,
    kind: 'human',
    layout: 'row',
  },
];

const WHATSAPP_URL = 'https://wa.me/918047110244';

const formatTimestamp = (iso?: string) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
};

const BUBBLE_HORIZONTAL_PADDING = 16 * 2;

// ── Hero: checkerboard banner + waving Caissa (Rive) + display heading ──
const CaissaHero = () => {
  const {isMobileOrTab} = useMediaQuery();
  const {RiveComponent} = useRive({
    src: caissahello_animation,
    autoplay: true,
    layout: new Layout({fit: Fit.Contain, alignment: Alignment.BottomCenter}),
  });
  return (
    <Box style={[styles.hero, isMobileOrTab && {height: 250}]}>
      <Box style={styles.heroBoard} />
      <Box style={styles.heroContent}>
        <Box
          style={[styles.heroAvatar, isMobileOrTab && {width: 190, height: 160}]}
          pointerEvents="none">
          {RiveComponent ? <RiveComponent /> : null}
        </Box>
        <CCText
          fontWeight="thunder"
          fontSize={isMobileOrTab ? FontSizes[30] : FontSizes[36]}
          color={T.textDark}
          textAlign="center"
          style={
            {
              // Lift the dark display text off the busy checkerboard.
              textShadowColor: 'rgba(255,255,255,0.55)',
              textShadowOffset: {width: 0, height: 1},
              textShadowRadius: 6,
            } as any
          }>
          Hello, I'm Caissa!
        </CCText>
      </Box>
    </Box>
  );
};

// ── Staggered entrance (fade + slide up) ──
const FadeUp = ({
  delay = 0,
  style,
  children,
}: {
  delay?: number;
  style?: any;
  children: React.ReactNode;
}) => {
  const v = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    Animated.timing(v, {
      toValue: 1,
      duration: 420,
      delay,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: false,
    }).start();
  }, [v, delay]);
  return (
    <Animated.View
      style={[
        {
          opacity: v,
          transform: [
            {translateY: v.interpolate({inputRange: [0, 1], outputRange: [14, 0]})},
          ],
        },
        style,
      ]}>
      {children}
    </Animated.View>
  );
};

// ── Looping rightward nudge — draws the eye to a "go" affordance (arrows,
// chevrons). Gentle ease in/out, small travel. ──
const NudgeRight = ({
  distance = 4,
  children,
}: {
  distance?: number;
  children: React.ReactNode;
}) => {
  const v = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(v, {
          toValue: 1,
          duration: 620,
          easing: Easing.inOut(Easing.quad),
          useNativeDriver: false,
        }),
        Animated.timing(v, {
          toValue: 0,
          duration: 620,
          easing: Easing.inOut(Easing.quad),
          useNativeDriver: false,
        }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [v]);
  return (
    <Animated.View
      style={{
        transform: [
          {translateX: v.interpolate({inputRange: [0, 1], outputRange: [0, distance]})},
        ],
      }}>
      {children}
    </Animated.View>
  );
};

// ── A picker card with a one-time highlight (gold border pulse + a single
// shimmer sweep) that runs once per card in sequence on open. No positional
// movement, so neighbouring cards never collide. Hover only re-shows the border
// glow (no shimmer). Handles the locked (non-CSOC) state too: dimmed, a lock
// badge, and a cursor-following tooltip — still gets the intro highlight.
const HIGHLIGHT_BORDER = '#FFD86B'; // warm gold, reads well on every card colour
const PickerCard = ({
  index,
  style,
  wrapperStyle,
  children,
  locked,
  lockColor,
  lockText,
  onPress,
}: {
  index: number;
  style?: any;
  wrapperStyle?: any;
  children: React.ReactNode;
  locked?: boolean;
  lockColor?: string;
  lockText?: string;
  onPress?: () => void;
}) => {
  const glow = useRef(new Animated.Value(0)).current;
  const shimmer = useRef(new Animated.Value(0)).current;
  const [showTip, setShowTip] = useState(false);

  // One-time intro: gold border fades in, a single shimmer sweeps L→R, border
  // fades out. Plays exactly once, sequenced by card index.
  useEffect(() => {
    glow.setValue(0);
    shimmer.setValue(0);
    const intro = Animated.parallel([
      Animated.sequence([
        Animated.timing(glow, {toValue: 1, duration: 260, useNativeDriver: false}),
        Animated.delay(620),
        Animated.timing(glow, {toValue: 0, duration: 460, useNativeDriver: false}),
      ]),
      Animated.timing(shimmer, {
        toValue: 1,
        duration: 950,
        easing: Easing.inOut(Easing.ease),
        useNativeDriver: false,
      }),
    ]);
    const t = setTimeout(() => intro.start(), 600 + index * 420);
    return () => {
      clearTimeout(t);
      intro.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  // Hover shows only the border glow — no shimmer replay.
  const onEnter = () =>
    Animated.timing(glow, {toValue: 1, duration: 160, useNativeDriver: false}).start();
  const onLeave = () =>
    Animated.timing(glow, {toValue: 0, duration: 240, useNativeDriver: false}).start();

  const borderColor = glow.interpolate({
    inputRange: [0, 1],
    outputRange: ['rgba(255,216,107,0)', HIGHLIGHT_BORDER],
  });
  const glowShadow = glow.interpolate({inputRange: [0, 1], outputRange: [0, 0.55]});
  const shimmerX = shimmer.interpolate({inputRange: [0, 1], outputRange: ['-140%', '180%']});

  const cardStyle = [
    style,
    {
      borderWidth: 2.5,
      borderColor,
      overflow: 'hidden',
      shadowColor: HIGHLIGHT_BORDER,
      shadowOpacity: glowShadow as any,
      shadowRadius: 16,
      shadowOffset: {width: 0, height: 0},
    },
    locked ? {opacity: 0.58} : null,
  ];
  const shimmerOverlay = (
    <Animated.View
      pointerEvents="none"
      style={{
        position: 'absolute',
        top: -20,
        bottom: -20,
        width: '45%',
        transform: [{translateX: shimmerX}, {rotate: '18deg'}],
      }}>
      <LinearGradient
        colors={['rgba(255,255,255,0)', 'rgba(255,255,255,0.4)', 'rgba(255,255,255,0)']}
        start={{x: 0, y: 0}}
        end={{x: 1, y: 0}}
        style={{flex: 1}}
      />
    </Animated.View>
  );

  if (locked) {
    return (
      <Box
        style={[{position: 'relative'}, wrapperStyle]}
        {...({
          onMouseEnter: () => {
            onEnter();
            setShowTip(true);
          },
          onMouseLeave: () => {
            setShowTip(false);
            onLeave();
          },
        } as any)}>
        <Animated.View pointerEvents="none" style={cardStyle}>
          {children}
          {shimmerOverlay}
        </Animated.View>
        <Box pointerEvents="none" style={styles.lockBadge}>
          <MaterialCommunityIcons name="lock" size={14} color={lockColor} />
        </Box>
        {showTip ? (
          <Box pointerEvents="none" style={styles.lockTooltipFloat}>
            <CCText fontSize={FontSizes[11]} color={T.white} style={{lineHeight: 16, textAlign: 'center'}}>
              {lockText}
            </CCText>
          </Box>
        ) : null}
      </Box>
    );
  }

  return (
    <TouchableOpacity onPress={onPress} activeOpacity={0.9} style={wrapperStyle}>
      <Animated.View {...({onMouseEnter: onEnter, onMouseLeave: onLeave} as any)} style={cardStyle}>
        {children}
        {shimmerOverlay}
      </Animated.View>
    </TouchableOpacity>
  );
};

// ── Animated "tap here" hand — nudges the user to click a card to proceed. ──
const TapNudge = () => {
  const v = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(v, {toValue: 1, duration: 480, easing: Easing.out(Easing.quad), useNativeDriver: false}),
        Animated.timing(v, {toValue: 0, duration: 420, easing: Easing.in(Easing.quad), useNativeDriver: false}),
        Animated.delay(520),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [v]);
  const translateY = v.interpolate({inputRange: [0, 1], outputRange: [0, 7]});
  const scale = v.interpolate({inputRange: [0, 1], outputRange: [1, 0.82]});
  const rippleOpacity = v.interpolate({inputRange: [0, 0.5, 1], outputRange: [0, 0.4, 0]});
  const rippleScale = v.interpolate({inputRange: [0, 1], outputRange: [0.4, 1.5]});
  return (
    <Box pointerEvents="none" style={styles.tapNudge}>
      <Animated.View
        style={[styles.tapNudgeRipple, {opacity: rippleOpacity, transform: [{scale: rippleScale}]}]}
      />
      <Animated.View style={{transform: [{translateY}, {scale}]}}>
        <MaterialCommunityIcons name="gesture-tap" size={30} color={T.white} />
      </Animated.View>
    </Box>
  );
};

// A top card (General Coach / Psychology Guide). When `locked` (a non-CSOC user
// on the Psychology card) it's dimmed with a lock badge and a cursor tooltip.
const TopCard = ({
  agent,
  index,
  locked,
  nudge,
  onPick,
}: {
  agent: Agent;
  index: number;
  locked: boolean;
  nudge?: boolean;
  onPick: (a: Agent) => void;
}) => (
  <PickerCard
    index={index}
    wrapperStyle={{flex: 1}}
    style={[styles.pickerCardTop, {backgroundColor: agent.color, ...(shadowFor(agent) as object)}]}
    locked={locked}
    lockColor={agent.color}
    lockText="Subscribe to the Caissa School of Chess to chat with the Psychology Guide."
    onPress={() => onPick(agent)}>
    {nudge ? <TapNudge /> : null}
    <Box>
      <Box style={styles.pickerAvatarLg}>
        <CCText fontSize={FontSizes[18]} fontWeight="bold" color={T.white}>
          {agent.initial}
        </CCText>
      </Box>
      <CCText fontSize={FontSizes[15]} fontWeight="semibold" color={T.white}>
        {agent.name}
      </CCText>
      <CCText
        fontSize={FontSizes[12]}
        color="rgba(255,255,255,0.82)"
        style={{marginTop: 4, marginBottom: 10}}>
        {agent.desc}
      </CCText>
    </Box>
    <Box type="row-flex-start" style={{alignItems: 'center'}}>
      {locked ? (
        <>
          <MaterialCommunityIcons name="lock" size={13} color={T.white} />
          <Spacer spacing={5} horizontal />
          <CCText fontSize={FontSizes[11]} fontWeight="semibold" color={T.white}>
            School of Chess students only
          </CCText>
        </>
      ) : (
        <Box type="row-flex-start" style={{alignItems: 'center', flexWrap: 'nowrap'}}>
          <CCText fontSize={FontSizes[11]} fontWeight="bold" color={T.yellow}>
            Start chat
          </CCText>
          <Spacer spacing={5} horizontal />
          <NudgeRight distance={4}>
            <MaterialCommunityIcons name="arrow-right" size={14} color={T.yellow} />
          </NudgeRight>
        </Box>
      )}
    </Box>
  </PickerCard>
);

// ── A "Try asking" starter row with hover feedback ──
const StarterCard = ({text, onPress}: {text: string; onPress: () => void}) => {
  const [hovered, handlers] = useHover();
  return (
    <TouchableOpacity onPress={onPress} activeOpacity={0.85}>
      <Box
        {...handlers}
        style={[
          styles.starterCard,
          hovered && {borderColor: T.yellowDark, backgroundColor: '#FFFDF5'},
        ]}>
        <CCText fontSize={FontSizes[14]} color={T.textDark} style={{flex: 1}}>
          {text}
        </CCText>
        <Box
          style={[styles.starterArrow, hovered && {transform: [{scale: 1.1}]}]}>
          <MaterialCommunityIcons name="send" size={13} color={T.brown} />
        </Box>
      </Box>
    </TouchableOpacity>
  );
};

const PHONE_NUMBER = '+91 8047110244';
const PHONE_DIAL = 'tel:+918047110244';

const PhoneChip = () => {
  const [copied, setCopied] = useState(false);
  const copy = (e: any) => {
    e.stopPropagation?.();
    navigator.clipboard?.writeText(PHONE_NUMBER).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  };
  const dial = (e: any) => {
    e.stopPropagation?.();
    Linking.openURL(PHONE_DIAL);
  };
  return (
    <Box style={{flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 6}}>
      <TouchableOpacity onPress={dial} hitSlop={6}>
        <CCText fontSize={FontSizes[12]} fontWeight="semibold" color={T.white}>
          WhatsApp Chat or Call — {PHONE_NUMBER}
        </CCText>
      </TouchableOpacity>
      <TouchableOpacity onPress={copy} hitSlop={8}>
        <MaterialCommunityIcons
          name={copied ? 'check' : 'content-copy'}
          size={14}
          color={copied ? '#A8F5C8' : 'rgba(255,255,255,0.85)'}
        />
      </TouchableOpacity>
    </Box>
  );
};

// ── Agent picker ──
const AgentPicker = ({
  onPick,
  isCsoc,
}: {
  onPick: (a: Agent) => void;
  isCsoc: boolean;
}) => {
  const topAgents = AGENTS.filter(a => a.layout === 'top');
  const rowAgents = AGENTS.filter(a => a.layout === 'row');
  return (
    <>
      <CCText fontSize={FontSizes[14]} color={T.textGrey} style={styles.pickerPrompt}>
        Who would you like to talk to today?
      </CCText>
      <Box style={styles.pickerRow}>
        {topAgents.map((a, i) => (
          <FadeUp key={a.id} delay={i * 70} style={{flex: 1}}>
            <TopCard
              agent={a}
              index={i}
              locked={a.id === 'psychology' && !isCsoc}
              nudge={i === 0}
              onPick={onPick}
            />
          </FadeUp>
        ))}
      </Box>
      {rowAgents.map((a, i) => (
        <FadeUp key={a.id} delay={(topAgents.length + i) * 70}>
          <PickerCard
            index={topAgents.length + i}
            onPress={() => onPick(a)}
            style={[styles.pickerCardRow, {backgroundColor: a.color, ...(shadowFor(a) as object)}]}>
            <Box style={styles.pickerAvatarRow}>
              {a.kind === 'human' ? (
                <MaterialCommunityIcons name="whatsapp" size={22} color={T.white} />
              ) : (
                <CCText fontSize={FontSizes[16]} fontWeight="bold" color={T.white}>
                  {a.initial}
                </CCText>
              )}
            </Box>
            <Spacer spacing={14} horizontal />
            <Box style={{flex: 1}}>
              <CCText fontSize={FontSizes[15]} fontWeight="semibold" color={T.white}>
                {a.name}
              </CCText>
              <CCText fontSize={FontSizes[12]} color="rgba(255,255,255,0.82)" style={{marginTop: 2}}>
                {a.desc}
              </CCText>
              {a.kind === 'human' ? <PhoneChip /> : null}
            </Box>
            <NudgeRight distance={5}>
              <Box style={[styles.pickerRowChevron, {backgroundColor: T.white}]}>
                <MaterialCommunityIcons name="chevron-right" size={20} color={a.colorDark} />
              </Box>
            </NudgeRight>
          </PickerCard>
        </FadeUp>
      ))}
    </>
  );
};


// Web 3D "hard" shadow beneath each agent card.
const shadowFor = (a: Agent) => ({
  boxShadow: `0 8px 0 0 ${a.colorDark}, 0 10px 20px rgba(77,63,55,0.18)`,
});

// ── Conversation header (back + agent + switcher) ──
// One switch target: hover scales it + shows the agent name; a locked target
// (Psychology for non-CSOC) is dimmed with a lock and can't be switched to.
const SwitcherDot = ({
  agent,
  locked,
  onPress,
}: {
  agent: Agent;
  locked: boolean;
  onPress: () => void;
}) => {
  const [hovered, handlers] = useHover();
  return (
    <Box style={{position: 'relative'}} {...handlers}>
      <TouchableOpacity onPress={onPress} disabled={locked} activeOpacity={0.8}>
        {/* Individual outlined chip: coloured border, fills with the agent
            colour on hover. No shared background pill. */}
        <Box
          style={[
            styles.switcherDot,
            {
              borderColor: agent.color,
              backgroundColor: hovered && !locked ? agent.color : T.white,
            },
            hovered && !locked && styles.switcherDotHover,
            locked && {opacity: 0.65},
          ]}>
          {agent.kind === 'human' ? (
            <MaterialCommunityIcons
              name="whatsapp"
              size={14}
              color={hovered && !locked ? T.white : agent.color}
            />
          ) : (
            <CCText
              fontSize={FontSizes[11]}
              fontWeight="bold"
              color={hovered && !locked ? T.white : agent.color}>
              {agent.initial}
            </CCText>
          )}
          {locked ? (
            <Box style={styles.switcherLock}>
              <MaterialCommunityIcons name="lock" size={9} color={T.white} />
            </Box>
          ) : null}
        </Box>
      </TouchableOpacity>
      {hovered ? (
        <Box pointerEvents="none" style={styles.switcherTooltip}>
          <CCText fontSize={FontSizes[10]} color={T.white} style={{whiteSpace: 'nowrap'} as any}>
            {locked ? 'School of Chess students only' : `Switch to ${agent.name}`}
          </CCText>
        </Box>
      ) : null}
    </Box>
  );
};

const ConvHeader = ({
  agent,
  onBack,
  onSwitch,
  isCsoc,
}: {
  agent: Agent;
  onBack: () => void;
  onSwitch: (a: Agent) => void;
  isCsoc: boolean;
}) => (
  <Box style={styles.convHeader}>
    <HoverBox onPress={onBack} style={styles.backBtn} hoverStyle={{backgroundColor: T.cream3}}>
      <MaterialCommunityIcons name="chevron-left" size={20} color={T.textDark} />
    </HoverBox>
    <Box style={[styles.convAvatar, {backgroundColor: agent.color}]}>
      {agent.kind === 'human' ? (
        <MaterialCommunityIcons name="whatsapp" size={20} color={T.white} />
      ) : (
        <CCText fontSize={FontSizes[15]} fontWeight="bold" color={T.white}>
          {agent.initial}
        </CCText>
      )}
    </Box>
    <CCText fontSize={FontSizes[16]} fontWeight="semibold" color={T.textDark} style={{flex: 1}}>
      {agent.name}
    </CCText>
    <Box style={styles.switcherWrap}>
      <CCText
        fontSize={FontSizes[10]}
        fontWeight="semibold"
        color={T.textMuted}
        style={styles.switcherLabel}>
        SWITCH
      </CCText>
      <MaterialCommunityIcons name="swap-horizontal" size={14} color={T.textMuted} />
      <Box style={styles.switcher}>
        {AGENTS.filter(a => a.id !== agent.id).map(a => (
          <SwitcherDot
            key={a.id}
            agent={a}
            locked={a.id === 'psychology' && !isCsoc}
            onPress={() => onSwitch(a)}
          />
        ))}
      </Box>
    </Box>
  </Box>
);

// ── One message bubble (+ reaction row for psychology answers) ──
const MessageBubble = ({
  msg,
  onReact,
}: {
  msg: textMsgProp;
  onReact?: (id: number, reaction: CaissaReaction | null) => void;
}) => {
  const isUser = msg.from === 'user';
  const [bubbleWidth, setBubbleWidth] = useState<number | null>(null);
  const showMeta = !isUser && !!msg.chatLogId;
  return (
    <Box style={[styles.msgRow, {alignItems: isUser ? 'flex-end' : 'flex-start'}]}>
      <Box
        onLayout={e => setBubbleWidth(e.nativeEvent.layout.width)}
        style={[
          styles.bubble,
          styles.shadowSoft,
          {backgroundColor: isUser ? T.cream3 : T.white},
        ]}>
        <CCParsedMessage
          text={msg.msg ?? ''}
          maxContentWidth={
            bubbleWidth ? bubbleWidth - BUBBLE_HORIZONTAL_PADDING : undefined
          }
        />
      </Box>
      {showMeta ? (
        <Box style={[styles.msgMeta, bubbleWidth ? {width: bubbleWidth} : null]}>
          <CCText fontSize={FontSizes[11]} color={T.textMuted}>
            {formatTimestamp(msg.timeStamp)}
          </CCText>
          <AnswerReactions
            selected={msg.reaction ?? null}
            onSelect={reaction => onReact?.(msg.id, reaction)}
          />
        </Box>
      ) : null}
    </Box>
  );
};

function ChatModal({
  openModal,
  handleClose,
  psychologyModeRequestId = 0,
  initialPsychologyMode = false,
}) {
  const [caissaInput, setCaissaInput] = useState('');
  const [messages, setMessages] = useState<textMsgProp[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);
  const scrollViewRef = useRef<ScrollView>(null);
  const lastAppliedPsychologyRequestId = useRef(psychologyModeRequestId);
  // Debounce guard: Enter can fire onSubmitEditing twice in the same tick, which
  // sent the message (and called the API) twice. Ignore repeats within 400ms.
  const lastSentRef = useRef(0);

  const planDetails = useUserPlanStore(s => s.planDetails);
  const isCsoc = selectIsCsocUser(planDetails);
  const {isMobileOrTab} = useMediaQuery();
  const padH = isMobileOrTab ? 16 : 28;

  const selectedAgent = AGENTS.find(a => a.id === selectedAgentId) || null;
  const isPsychologyMode = selectedAgent?.psychology === true;

  useEffect(() => {
    if (scrollViewRef.current) {
      scrollViewRef.current.scrollToEnd({animated: true});
    }
  }, [messages, isLoading, selectedAgentId]);

  // Reset to the picker on close only when nothing has been said yet.
  useEffect(() => {
    if (!openModal && messages.length === 0) {
      setSelectedAgentId(null);
    }
  }, [openModal, messages.length]);

  // The CSOC "Talk to Psychology Expert" CTA bumps the request id with
  // initialPsychologyMode=true -> jump straight into the Psychology agent.
  useEffect(() => {
    if (psychologyModeRequestId !== lastAppliedPsychologyRequestId.current) {
      lastAppliedPsychologyRequestId.current = psychologyModeRequestId;
      setSelectedAgentId(initialPsychologyMode ? 'psychology' : null);
    }
  }, [psychologyModeRequestId, initialPsychologyMode]);

  const {mutate: getCaissaResponse} = useGetCaissaChatbotResponse(response => {
    setMessages(prevMessages => [
      ...prevMessages,
      {
        id: prevMessages.length + 1,
        timeStamp: new Date().toISOString(),
        msg: response?.response,
        type: 'text',
        from: 'caissa',
        chatLogId: response?.chat_log_id ?? null,
        reaction: null,
      },
    ]);
    setIsLoading(false);
  });

  const {mutate: submitReaction} = useSubmitCaissaReaction();

  const handleReact = (id: number, reaction: CaissaReaction | null) => {
    const target = messages.find(m => m.id === id);
    if (!target?.chatLogId) return;
    setMessages(prev => prev.map(m => (m.id === id ? {...m, reaction} : m)));
    submitReaction({chat_log_id: target.chatLogId, reaction});
  };

  const sendMessage = (message: string) => {
    const now = Date.now();
    if (now - lastSentRef.current < 400) return; // debounce double-fire
    if (message.trim() && !isLoading) {
      lastSentRef.current = now;
      setMessages(prevMessages => [
        ...prevMessages,
        {
          id: prevMessages.length + 1,
          timeStamp: new Date().toISOString(),
          msg: message,
          type: 'text',
          from: 'user',
        },
      ]);
      setIsLoading(true);
      getCaissaResponse({user_message: message, is_psychology_mode: isPsychologyMode});
      setCaissaInput('');
      Keyboard.dismiss();
    }
  };

  const openWhatsApp = () =>
    Linking.openURL(WHATSAPP_URL).catch(err =>
      console.error('Error opening WhatsApp:', err),
    );

  // Human agent hands straight off to WhatsApp; chat agents open a conversation.
  const activateAgent = (a: Agent) => {
    // Psychology Guide is locked for non-CSOC users.
    if (a.id === 'psychology' && !isCsoc) return;
    if (a.kind === 'human') {
      openWhatsApp();
      return;
    }
    setSelectedAgentId(a.id);
  };

  const isChatAgent = selectedAgent?.kind === 'chat';
  const showStarters = isChatAgent && messages.length === 0;

  return (
    <SlideModal
      type="side"
      visible={openModal}
      onClose={handleClose}
      customHeaderStyle={{display: 'none'}}>
      <Box style={styles.panel}>
        <TouchableOpacity onPress={handleClose} style={styles.closeBtn}>
          <Ionicons name="close" size={20} color={T.textDark} />
        </TouchableOpacity>

        <ScrollView
          ref={scrollViewRef}
          style={{flex: 1, width: '100%'}}
          contentContainerStyle={{paddingBottom: 8}}>
          <CaissaHero />

          <Box style={[styles.body, {paddingHorizontal: padH}]}>
            {!selectedAgent ? (
              <AgentPicker onPick={activateAgent} isCsoc={isCsoc} />
            ) : (
              <>
                <ConvHeader
                  agent={selectedAgent}
                  onBack={() => setSelectedAgentId(null)}
                  onSwitch={activateAgent}
                  isCsoc={isCsoc}
                />

                {showStarters && selectedAgent.starters ? (
                  <>
                    <CCText
                      fontSize={FontSizes[11]}
                      fontWeight="semibold"
                      color={T.textGrey}
                      style={styles.eyebrow}>
                      TRY ASKING
                    </CCText>
                    {selectedAgent.starters.map((s, index) => (
                      <StarterCard key={index} text={s} onPress={() => sendMessage(s)} />
                    ))}
                  </>
                ) : null}

                {messages.map(m => (
                  <MessageBubble key={m.id} msg={m} onReact={handleReact} />
                ))}

                {isLoading ? (
                  <Box type="row-flex-start" style={{alignItems: 'center', marginTop: 8}}>
                    <CCText fontSize={FontSizes[13]} color={T.textGrey}>
                      Caissa is thinking
                    </CCText>
                    <Spacer spacing={6} horizontal />
                    <TypingDots />
                  </Box>
                ) : null}
              </>
            )}
          </Box>
        </ScrollView>

        {/* Input pill — only for a chat agent */}
        {isChatAgent ? (
          <Box style={{paddingHorizontal: padH, paddingBottom: 24, paddingTop: 8}}>
            <Box style={[styles.inputPill, styles.shadowSoft]}>
              <TextInput
                placeholder="Ask Caissa anything…"
                placeholderTextColor={T.textMuted}
                value={caissaInput}
                onChangeText={setCaissaInput}
                onSubmitEditing={() => sendMessage(caissaInput)}
                style={
                  {
                    flex: 1,
                    fontFamily: 'DMSans-Regular',
                    fontSize: 14,
                    color: T.text,
                    paddingVertical: 8,
                    outlineStyle: 'none',
                    borderWidth: 0,
                    backgroundColor: 'transparent',
                  } as any
                }
              />
              <HoverBox
                style={[styles.sendBtn, isLoading && {opacity: 0.6}]}
                hoverStyle={{backgroundColor: T.yellowDark, transform: [{scale: 1.06}]}}
                onPress={() => sendMessage(caissaInput)}
                disabled={isLoading}>
                <MaterialCommunityIcons name="send" size={18} color={T.brown} />
              </HoverBox>
            </Box>
          </Box>
        ) : null}
      </Box>
    </SlideModal>
  );
}

export default ChatModal;
